import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { autocomplete, getAlgoliaResults } from "@algolia/autocomplete-js";
import { createAutocomplete } from "@algolia/autocomplete-core";
import { ConvertToFloat, isValidArray } from "../../Layout/Common/common";
import { Link, useNavigate } from "react-router-dom";
import { ItemContainer } from "./itemContainer";
import "react-loading-skeleton/dist/skeleton.css";
import { AutosuggestPopupItemSkeleton } from "../../Skeleton/autoSuggestPopup/autosuggestPopupItemSkeleton";

import { CategorySuggestion } from "./categorySuggestion";
import { QuerySuggestion } from "./querySuggestion";
import { PAGES } from "../../routeConfig";
import { AutoCompleteResult } from "./autoCompleteResult";
import { searchIndex } from "../../Layout/Common/algoliaInstantLib";
import algoliasearch from "algoliasearch";

const searchClient = algoliasearch(
  `${process.env.REACT_APP_ALGOLIA_APP_ID}`,
  `${process.env.REACT_APP_ALGOLIA_SEARCH_KEY}`,
);

const indexName = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}`;
const newproductindexName = `${process.env.REACT_APP_ALGOLIA_NEWPRODUCT_INDEX_NAME}`;
const suggestionIndexName = `${process.env.REACT_APP_ALGOLIA_SUGGESTION_INDEX}`;

export const AutoCompleteSearchBoxMobile = ({ isOpen, handleHide }) => {
  const navigate = useNavigate();
  // Two values on purpose. `query` is the input's displayed value and what Enter / the magnifier
  // navigate with, so it has to track every keystroke. `searchQuery` is the debounced copy that
  // actually hits Algolia - debouncing `query` itself would make the field lag while typing.
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Each committed query fires two uncancellable Algolia requests. Un-debounced that was two per
  // keystroke, competing for the browser's connections on the device least able to spare them.
  const debouncedSetSearchQuery = useMemo(() => debounce((val) => setSearchQuery(val), 300), []);
  useEffect(() => () => debouncedSetSearchQuery.cancel(), [debouncedSetSearchQuery]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 1000);
  }, [isOpen]);

  useEffect(() => {
    debouncedSetSearchQuery(query);
  }, [query, debouncedSetSearchQuery]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchStr = params.get("q");
    // Cancel any pending keystroke debounce so landing on /search?q=... fetches straight away.
    debouncedSetSearchQuery.cancel();
    setQuery(searchStr ?? "");
    setSearchQuery(searchStr ?? "");
  }, [window.location.search]);

  useEffect(() => {
    const fetchResults = async () => {
      const [mainRes, suggestionRes] = await Promise.all([
        searchIndex(indexName, { query: searchQuery, hitsPerPage: 20 }),
        searchIndex(suggestionIndexName, { query: searchQuery, hitsPerPage: 20 }),
      ]);

      setHits(mainRes.hits);
      setSuggestions(
        suggestionRes.hits.map((hit) => ({
          value: hit._highlightResult.query.value,
          query: hit.query,
          categories: hit[indexName]?.facets?.exact_matches?.category?.map((c) => c.value) || [],
        })),
      );
    };
    fetchResults();
  }, [searchQuery]);

  const handleSearchClick = () => {
    navigate(PAGES.SEARCH + "?q=" + encodeURIComponent(query));
    handleHide();
  };

  return (
    <Fragment>
      <div className="row">
        <div className="col-lg-12">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Drop focus, then go through handleSearchClick so Enter and the magnifier icon
              // follow the exact same path. Submitting used to navigate without handleHide(),
              // leaving the full-screen search overlay open on top of the results.
              inputRef.current?.blur();
              handleSearchClick();
            }}
          >
            <div className="cc-mb-search">
              <div className="close-search" onClick={handleHide}>
                <i id="close-search2" className="fa-regular fa-circle-xmark d-none d-lg-block" />
                <div id="close-search-mob" className="mobile-close-search d-block d-lg-none">
                  <span className="wes-down-arrow" />
                  <small>Close</small>
                </div>
              </div>

              <div className="suggestion-input button-effect d-block d-lg-none">
                <div className="cc-mob-sugg position-relative">
                  <input
                    ref={inputRef}
                    type="text"
                    className="form-control"
                    value={query ?? ""}
                    onChange={(e) => {
                      setQuery(e.target.value);
                    }}
                    placeholder="Search Here..."
                    autoComplete="off"
                  />
                  <span className="wes-search" onClick={handleSearchClick} />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <AutoCompleteResult hits={hits} query={query} suggestions={suggestions} />
    </Fragment>
  );
};
